import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { PageHeader, Spinner, Surface, CopyButton } from "@/components/common";
import { RefinePanel } from "@/components/RefinePanel";
import { AttachmentUploader } from "@/components/AttachmentUploader";
import { LanguageSelect } from "@/components/LanguageSelect";
import { useLocale } from "@/context/LocaleContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ThumbsUp, ThumbsDown, TrendingUp, Radio, Sparkles, FileText, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";

function ScoreRing({ score }) {
  const r = 52, c = 2 * Math.PI * r;
  const color = score >= 70 ? "#EDEDED" : score >= 40 ? "#A1A1AA" : "#52525B";
  return (
    <div className="relative h-36 w-36" data-testid="press-score-ring">
      <svg className="h-36 w-36 -rotate-90" viewBox="0 0 120 120">
        <circle cx="60" cy="60" r={r} fill="none" stroke="#27272A" strokeWidth="6" />
        <motion.circle cx="60" cy="60" r={r} fill="none" stroke={color} strokeWidth="6" strokeLinecap="round"
          strokeDasharray={c} initial={{ strokeDashoffset: c }} animate={{ strokeDashoffset: c - (score / 100) * c }}
          transition={{ duration: 1, ease: "easeOut" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-display text-4xl font-medium">{score}</span>
        <span className="text-xs text-muted-foreground">/ 100</span>
      </div>
    </div>
  );
}

function List({ icon: Icon, title, items, testid }) {
  if (!items?.length) return null;
  return (
    <Surface testid={testid}>
      <div className="flex items-center gap-2 mb-3"><Icon className="h-4 w-4 text-muted-foreground" /><h3 className="font-display font-medium">{title}</h3></div>
      <ul className="space-y-2">{items.map((it, i) => <li key={i} className="text-sm text-muted-foreground leading-relaxed flex gap-2"><span className="text-foreground">·</span>{it}</li>)}</ul>
    </Surface>
  );
}

export default function Press() {
  const location = useLocation();
  const { t, contentLang } = useLocale();
  const [story, setStory] = useState("");
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [phase, setPhase] = useState("analyze"); // analyze | develop | release
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState("");
  const [release, setRelease] = useState(null);
  const [releaseProjectId, setReleaseProjectId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [language, setLanguage] = useState(contentLang);
  const attIds = () => attachments.map((a) => a.id);

  useEffect(() => {
    if (location.state?.topic) { setStory(location.state.topic); analyze(location.state.topic); }
    // eslint-disable-next-line
  }, []);

  const analyze = async (arg) => {
    const value = typeof arg === "string" ? arg : story;
    if (!value.trim()) return toast.error(t("Beskriv historien"));
    setLoading(true); setAnalysis(null); setRelease(null); setQuestions([]); setPhase("analyze");
    try {
      const { data } = await api.post("/press/analyze", { story: value, language, attachment_ids: attIds() });
      setAnalysis(data.result);
    } catch (err) { toast.error(apiError(err)); } finally { setLoading(false); }
  };

  const develop = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/press/develop", { story, language, attachment_ids: attIds() });
      setQuestions(data.result.questions || []);
      setPhase("develop");
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const write = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/press/write", { story, extra: answers, language, attachment_ids: attIds() });
      setRelease(data.result);
      setReleaseProjectId(data.project_id);
      setPhase("release");
      toast.success("Pressemeddelelse klar — gemt automatisk");
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const releaseText = release ? `${release.headline}\n\n${release.subheadline}\n\n${release.body}` : "";

  return (
    <div>
      <PageHeader overline="Pressepotentiale" title="Er historien værd at sende ud?"
        subtitle="Vi vurderer historien ud fra journalistiske nyhedskriterier — før vi skriver noget." />

      <div className="space-y-3 mb-8">
        <Textarea data-testid="press-story" value={story} onChange={(e) => setStory(e.target.value)}
          rows={3} placeholder={t('Fx "Vi har ansat en ny direktør" eller "Vi har udviklet et nyt produkt"')} />
        <AttachmentUploader files={attachments} onChange={setAttachments} disabled={loading} />
        <div className="flex flex-wrap items-center gap-3">
          <LanguageSelect value={language} onChange={setLanguage} disabled={loading} testid="press-language" />
          <Button onClick={() => analyze()} disabled={loading} className="gap-2" data-testid="press-analyze-btn">
            <Sparkles className="h-4 w-4" /> {t("Vurdér pressepotentiale")}
          </Button>
        </div>
      </div>

      {loading && <Spinner label="Vurderer historien..." />}

      {analysis && (
        <div className="space-y-4" data-testid="press-analysis">
          <Surface className="flex flex-col sm:flex-row items-center gap-6">
            <ScoreRing score={analysis.score} />
            <div className="flex-1 text-center sm:text-left">
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Samlet score</p>
              <p className="font-display text-xl font-medium leading-snug" data-testid="press-verdict">{analysis.verdict}</p>
            </div>
          </Surface>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <List icon={ThumbsUp} title={t("Det taler for historien")} items={analysis.pros} testid="press-pros" />
            <List icon={ThumbsDown} title={t("Det trækker ned")} items={analysis.cons} testid="press-cons" />
            <List icon={TrendingUp} title={t("Sådan kan historien blive stærkere")} items={analysis.improvements} testid="press-improvements" />
            <List icon={Radio} title={t("Relevante medier")} items={analysis.media} testid="press-media" />
          </div>
          <List icon={Sparkles} title={t("Stærkeste vinkler")} items={analysis.angles} testid="press-angles" />

          {phase === "analyze" && (
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <Button onClick={write} disabled={busy} className="gap-2" data-testid="press-write-btn"><FileText className="h-4 w-4" /> {t("Skriv pressemeddelelse")}</Button>
              <Button onClick={develop} disabled={busy} variant="outline" className="gap-2" data-testid="press-develop-btn"><Wand2 className="h-4 w-4" /> {t("Hjælp mig med at udvikle historien")}</Button>
            </div>
          )}

          {phase === "develop" && (
            <Surface testid="press-develop-block">
              <h3 className="font-display font-medium mb-3">{t("Svar på disse spørgsmål, så gør vi historien stærkere")}</h3>
              <ul className="space-y-2 mb-4">{questions.map((q, i) => <li key={i} className="text-sm text-muted-foreground flex gap-2"><span className="font-mono text-xs mt-0.5">{i + 1}.</span>{q}</li>)}</ul>
              <Textarea value={answers} onChange={(e) => setAnswers(e.target.value)} rows={5} placeholder={t("Skriv dine svar her...")} data-testid="press-answers" />
              <Button onClick={write} disabled={busy} className="mt-3 gap-2" data-testid="press-write-after-develop-btn"><FileText className="h-4 w-4" /> {t("Skriv pressemeddelelse nu")}</Button>
            </Surface>
          )}

          {busy && <Spinner label="Arbejder på historien..." />}

          {release && (
            <Surface testid="press-release">
              <div className="flex justify-end mb-3"><CopyButton text={releaseText} testid="press-release-copy" /></div>
              <h2 className="font-display text-2xl font-medium tracking-tight">{release.headline}</h2>
              <p className="text-muted-foreground mt-2 mb-4">{release.subheadline}</p>
              <div className="text-sm leading-relaxed whitespace-pre-wrap">{release.body}</div>
            </Surface>
          )}
          {release && (
            <RefinePanel result={release} setResult={setRelease} module="press" projectId={releaseProjectId}
              quick={["Gør den kortere", "Fremhæv nyhedsværdien", "Mere citatvenlig", "Neutral tone", "Skarpere rubrik"].map((q) => t(q))} />
          )}
        </div>
      )}
    </div>
  );
}
